import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/primitives/utils"

function Tabs({ className, orientation = "horizontal", ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn("group/tabs flex gap-2 data-horizontal:flex-col", className)}
      {...props}
    />
  )
}

/*
 * beacon: two shapes. `segmented` (the default) is the segmented control: a muted track whose
 * active segment rests on it, raised; it is used without panels too, `onValueChange` driving the
 * state. `line` is a row of quiet tabs whose current one takes the selected fill, as in a page
 * header. On touch each grows a step, to 36px, every tab's hit area reaching 44px.
 */
const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col",
  {
    variants: {
      variant: {
        segmented: "h-7 rounded-md bg-muted p-0.5 touch:h-9",
        line: "gap-1 bg-transparent",
      },
    },
    defaultVariants: {
      variant: "segmented",
    },
  },
)

function TabsList({
  className,
  variant = "segmented",
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        // beacon: 13px medium, muted until hovered or active, with a 2px focus ring and 14px
        // icons. The two shapes differ only in size and in how the active one is marked: the
        // active segment takes its own fill, lighter than the muted track in both schemes. Its
        // hairline is a ring too, so the focus ring is restated for it, or the hairline would win
        // and a keyboard couldn't see which segment it is on (a segmented control focuses the
        // active one).
        "relative inline-flex items-center justify-center gap-1.5 text-ui font-medium whitespace-nowrap text-muted-foreground transition-[color,background-color,box-shadow] duration-100 ease-snappy outline-none group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-active:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        "group-data-[variant=segmented]/tabs-list:h-6 group-data-[variant=segmented]/tabs-list:flex-1 touch:group-data-[variant=segmented]/tabs-list:h-8 group-data-[variant=segmented]/tabs-list:touch-target group-data-[variant=segmented]/tabs-list:rounded-sm group-data-[variant=segmented]/tabs-list:px-2.5 group-data-[variant=segmented]/tabs-list:data-active:bg-segment-active group-data-[variant=segmented]/tabs-list:data-active:shadow-raised group-data-[variant=segmented]/tabs-list:data-active:ring-1 group-data-[variant=segmented]/tabs-list:data-active:ring-border-subtle group-data-[variant=segmented]/tabs-list:data-active:focus-visible:ring-2 group-data-[variant=segmented]/tabs-list:data-active:focus-visible:ring-ring",
        "group-data-[variant=line]/tabs-list:h-7 touch:group-data-[variant=line]/tabs-list:h-9 group-data-[variant=line]/tabs-list:touch-target group-data-[variant=line]/tabs-list:rounded-md group-data-[variant=line]/tabs-list:px-2 group-data-[variant=line]/tabs-list:data-active:bg-selected",
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      // beacon: UI text.
      className={cn("flex-1 text-ui outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
